/**
 * The ONE state a hierarchy detail route renders for the API's answer (f9.2).
 *
 * The request is always issued and the state is chosen from the response;
 * the client never pre-checks an id -- `scope` carries no ids precisely so
 * that it cannot.
 *
 * * `404` -- **"This record cannot be opened"**. The copy deliberately does
 *   NOT distinguish a record that does not exist from one the caller may not
 *   see: that indistinguishability is why Q-f9.1-3 chose `404` over `403`,
 *   and wording like "you may not have permission" would hand back the
 *   enumeration oracle in prose. All four levels render this same state, so
 *   a fifth cannot arrive with softer copy.
 * * `403` -- **"Not permitted"**, unchanged in meaning from f1.3: *you may not
 *   do this at all* is not sensitive. It never triggers re-authentication;
 *   only a `401` does (f1.2).
 * * anything else -- a failure, with a retry.
 */

import { AlertTriangle, ArrowLeft, FileX, RotateCw, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { ApiError } from '@/api/client';

export type DetailFailure = 'unavailable' | 'refused' | 'failed';

/** Which state a detail query's failure renders. Read from the response only. */
export function detailFailureOf(error: unknown): DetailFailure {
  if (error instanceof ApiError && error.status === 404) return 'unavailable';
  if (error instanceof ApiError && error.status === 403) return 'refused';
  return 'failed';
}

const COPY: Readonly<Record<DetailFailure, { title: string; body: string }>> = {
  unavailable: {
    title: 'This record cannot be opened',
    body: 'Atlas could not open the record at this address. Check the link, or go back to the list.',
  },
  refused: {
    title: 'Not permitted',
    body: 'The server refuses this for your account, and records the refusal in the audit log.',
  },
  failed: {
    title: 'This record could not be loaded',
    body: 'Something went wrong while loading it. Try again in a moment.',
  },
};

const ICONS: Readonly<Record<DetailFailure, ReactNode>> = {
  unavailable: <FileX aria-hidden="true" />,
  refused: <ShieldAlert aria-hidden="true" />,
  failed: <AlertTriangle aria-hidden="true" />,
};

export function RecordUnavailable({
  error,
  backTo,
  backLabel,
  onRetry,
}: {
  error: unknown;
  backTo: string;
  backLabel: string;
  onRetry?: () => void;
}) {
  const kind = detailFailureOf(error);
  const { title, body } = COPY[kind];
  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">{title}</h1>
        </div>
        <div className="page-actions">
          <Link className="btn btn-secondary btn-sm" to={backTo}>
            <ArrowLeft aria-hidden="true" /> {backLabel}
          </Link>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="empty">
            <div className={`e-icon${kind === 'unavailable' ? '' : ' e-icon-warning'}`}>
              {ICONS[kind]}
            </div>
            <h3>{title}</h3>
            <p>{body}</p>
            {kind === 'failed' && onRetry && (
              <p className="empty-actions">
                <button type="button" className="btn btn-secondary" onClick={onRetry}>
                  <RotateCw aria-hidden="true" /> Retry
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
